// New functions using the new CreditsInfo model

import { CreditsInfo } from "data/models";

export const roundDownOverageDollarsLimit = (
  newOverageDollarsLimit: number,
  creditsInfo: CreditsInfo
) => {
  if (
    newOverageDollarsLimit === 0 ||
    creditsInfo.overageDollarsPerCreditsBlock === 0
  ) {
    return 0;
  } else {
    return (
      newOverageDollarsLimit -
      (newOverageDollarsLimit % creditsInfo.overageDollarsPerCreditsBlock)
    );
  }
};

/**
 * Convert an Overage Dollars Limit to its Credit Amount
 * - i.e. $100 limit, $10 per credit block, 1000 credits per block = 10,000 credits
 * @param overageDollarsLimit
 * @param creditsInfo
 * @returns
 */
export const convertOverageDollarsLimitToCredits = (
  overageDollarsLimit: number,
  creditsInfo: CreditsInfo
) => {
  return (
    (overageDollarsLimit / creditsInfo.overageDollarsPerCreditsBlock) *
    creditsInfo.overageCreditsBlock
  );
};

interface PlanDetails {
  overageRate: number;
  planCredits: number;
}

/**
 * Round down the overage dollar limit to the nearest dollar block of 1000 credits.
 * @param limit The dollar amount
 * @param overageRate The overage rate ($/credit)
 */
const roundDownOverageDollarLimit = (limit: number, overageRate: number) => {
  const ratePer1000Credits = overageRate * 1000;
  return limit - (limit % ratePer1000Credits);
};

/**
 * Round up the overage dollar limit to the nearest dollar block of 1000 credits.
 * @param limit The dollar amount
 * @param overageRate The overage rate ($/credit)
 */
const roundUpOverageDollarLimit = (limit: number, overageRate: number) => {
  const ratePer1000Credits = overageRate * 1000;
  if (limit % ratePer1000Credits === 0) {
    return limit;
  } else {
    return limit + (ratePer1000Credits - (limit % ratePer1000Credits));
  }
};

/**
 * Round down to the nearest lot of target
 * For example: roundDownToNearest(45, 10) = 40
 * @param value The value to be rounded
 * @param target The target to round to
 */
const roundDownToNearest = (value: number, target: number) => {
  return value - (value % target);
};

const roundDownOverageBudget = (
  overageBudget: number,
  planDetails: PlanDetails
) => {
  // Round down the dollar amount to the closest block of 1000 credits
  //return overageBudget - (overageBudget % planDetails.overageRate);

  const credits = overageBudget / planDetails.overageRate;
  const roundedCredits = Math.floor(credits / 1000) * 1000;
  const roundedCost = roundedCredits * planDetails.overageRate;
  return roundedCost;
};

const roundDownOverageCredit = (overageCredits: number) => {
  // Round down the credit amount to the closest block of 1000 credits
  return overageCredits - (overageCredits % 1000);
};

const calculateOverageCredits = (
  overageDollars: number = 0,
  planDetails: PlanDetails
) => {
  // Calculate how many overage credits the user will have
  return Math.floor(overageDollars / planDetails.overageRate);
};

const calculateOverageDollars = (
  overageCredits: number = 0,
  planDetails: PlanDetails
) => {
  // Calculate how many overage dollars the user will have
  return Math.floor((overageCredits / 1000) * planDetails.overageRate);
};

const SECONDS_PER_DAY = 86400;
const MIN_DAYS_FOR_PROJECTION = 3;

interface UsageProjection {
  projectedCredits: number;
  daysElapsed: number;
  daysInPeriod: number;
  dailyBurn: number;
}

/**
 * Naïve linear projection of end-of-period credit usage.
 * Returns null if the period has not been running long enough to produce a
 * meaningful estimate (see MIN_DAYS_FOR_PROJECTION).
 *
 * @param spent credits already consumed this period
 * @param currentPeriodStart unix seconds — start of the current billing period
 * @param renewalDate unix seconds — end of the current billing period
 * @param now unix seconds (defaults to Date.now())
 */
const projectPeriodCreditUsage = (
  spent: number,
  currentPeriodStart: number,
  renewalDate: number,
  now: number = Math.floor(Date.now() / 1000)
): UsageProjection | null => {
  const elapsedSeconds = now - currentPeriodStart;
  const totalSeconds = renewalDate - currentPeriodStart;
  if (elapsedSeconds <= 0 || totalSeconds <= 0) return null;

  const daysElapsed = elapsedSeconds / SECONDS_PER_DAY;
  const daysInPeriod = totalSeconds / SECONDS_PER_DAY;

  if (daysElapsed < MIN_DAYS_FOR_PROJECTION) return null;

  const dailyBurn = spent / daysElapsed;
  const projectedCredits = Math.round(dailyBurn * daysInPeriod);

  return { projectedCredits, daysElapsed, daysInPeriod, dailyBurn };
};

export {
  calculateOverageCredits,
  calculateOverageDollars,
  projectPeriodCreditUsage,
  roundDownOverageBudget,
  roundDownOverageCredit,
  roundDownOverageDollarLimit,
  roundDownToNearest,
  roundUpOverageDollarLimit,
};
export type { UsageProjection };

export type { PlanDetails };
